import { CommonModule } from '@angular/common';
import { Component, type OnDestroy, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TimeSeriesPivotTableDataService } from '@backoffice-frontend/controlling/domain';
import type { TimeSeriesIdentifierCategory } from '@clean-code/models';
import {
  convertUtcToLocalTimezoneDate,
  indicate,
} from '@clean-code/shared/common';
import { ActionButtonModule } from '@clean-code/shared/components/ui-action-button';
import {
  UtilDateModule,
  provideCustomNativeDateAdapter,
} from '@clean-code/shared/util/util-date';
import { NgSelectModule } from '@ng-select/ng-select';
import { DxDateBoxModule } from 'devextreme-angular';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  finalize,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { PivotTableService } from '../../data/pivot-table.service';
@Component({
  selector: 'feature-pivot-table-add-row-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,

    NgSelectModule,
    DxDateBoxModule,

    ActionButtonModule,
    MatSelectModule,
    UtilDateModule,
  ],
  templateUrl: './add-row-dialog.component.html',
  styleUrl: './add-row-dialog.component.scss',
  providers: [provideCustomNativeDateAdapter()],
})
export class AddRowDialogComponent implements OnDestroy {
  public columns = [
    { name: 'settlementPrice', label: 'Settlement Price' },
    { name: 'openPrice', label: 'Open Price' },
    { name: 'highPrice', label: 'High Price' },
    { name: 'lowPrice', label: 'Low Price' },
    { name: 'volume', label: 'Volume' },
  ];
  protected service = inject(TimeSeriesPivotTableDataService);
  protected tableService = inject(PivotTableService);
  private dialogRef = inject(MatDialogRef<AddRowDialogComponent>);

  public formGroup = new FormGroup({
    identifier: new UntypedFormControl(null, Validators.required),
    date: new FormControl<Date | null>(new Date(), Validators.required),
    timestamp: new FormControl<Date | null>(
      { value: new Date(), disabled: true },
      Validators.required,
    ),
    unit: new UntypedFormControl(
      { value: null, disabled: true },
      Validators.required,
    ),
    columnName: new UntypedFormControl(
      { value: null, disabled: true },
      Validators.required,
    ),
    identifierCategoriesFormControl: new UntypedFormControl(
      null,
      Validators.required,
    ),
    cellValue: new UntypedFormControl(
      { value: null, disabled: true },
      Validators.required,
    ),
  });

  protected closeSubject = new Subject<void>();

  public dataExists$ = combineLatest([
    this.formGroup.controls.identifier.valueChanges,
    this.formGroup.controls.date.valueChanges.pipe(
      startWith(this.formGroup.controls.date.value),
    ),
  ]).pipe(
    switchMap(([identifier, date]) =>
      this.service.checkIfCellDataExists$(identifier, date).pipe(
        tap((x) => {
          if (x === false && identifier !== null && date !== null) {
            this.formGroup.controls.cellValue.enable();
            this.formGroup.controls.columnName.enable();
            this.formGroup.controls.unit.enable();
            this.formGroup.controls.timestamp.enable();
          } else {
            this.formGroup.controls.cellValue.disable();
            this.formGroup.controls.columnName.disable();
            this.formGroup.controls.unit.disable();
            this.formGroup.controls.timestamp.disable();
          }
        }),
      ),
    ),
  );

  public units$ = this.service.getAllControllingUnits$();

  public timeSeriesIdentifierCategoriesLocalized$ =
    this.service.getAllTimeSeriesIdentifierCategoriesLocalized$();

  public identifierIsLoading$ = new BehaviorSubject<boolean>(false);
  public identifiers$ =
    this.formGroup.controls.identifierCategoriesFormControl.valueChanges.pipe(
      switchMap((category: TimeSeriesIdentifierCategory) =>
        this.service.getAllControllingIdentifiers$(category.id).pipe(
          tap((x) => {
            if (x.length > 0) {
              this.formGroup.controls.identifier.enable();
            } else {
              this.formGroup.controls.identifier.disable();
            }
          }),
          indicate(this.identifierIsLoading$),
        ),
      ),
    );

  private timeStampCheck = combineLatest([
    this.formGroup.controls.date.valueChanges.pipe(startWith(new Date())),
    this.formGroup.controls.identifier.valueChanges,
  ]).pipe(
    switchMap(([date, identifier]) =>
      this.service.getTimeStamp$(identifier, date).pipe(
        tap((date: Date) => {
          this.formGroup.controls.timestamp.setValue(date);
        }),
      ),
    ),
    takeUntil(this.closeSubject),
  );

  constructor() {
    this.formGroup.controls.identifier.disable();

    this.timeStampCheck.subscribe();
  }

  disableAddButton = false;
  addCellData() {
    this.disableAddButton = true;

    const date = convertUtcToLocalTimezoneDate(
      this.formGroup.controls.date.value,
    );
    const symbol = this.formGroup.controls.identifier.value;
    const unit = this.formGroup.controls.unit.value;
    const value = this.formGroup.controls.cellValue.value;
    const colName = this.formGroup.controls.columnName.value;

    this.service
      .add$(
        colName,
        +value,
        date,
        symbol,
        unit,
        this.formGroup.controls.timestamp.value,
      )
      .pipe(
        tap(() => {
          this.tableService.setRefreshTable(true);
          this.disableAddButton = false;
        }),
        finalize(() => {
          this.dialogRef.close();
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.closeSubject.next();
    this.closeSubject.complete();
  }
}
